/**
 * @fileoverview PersistAdapter contract + session adapter factory.
 * @module inspection-renderer/adapters
 *
 * Responsibilities:
 * - Define the host-injected `PersistAdapter` shape consumed by every
 *   widget surface (`<InspectionRenderer>`, `<ViolationCaptureBlock>`,
 *   contacts panels and other module surfaces. Two factories ship in-package:
 *   - `createSessionAdapter()` — pure in-memory, preview only (D-0.14).
 *   - `createDexieAdapter` — provided by each consuming app (Phase 5,
 *     §8.2). The renderer never imports Dexie directly; the host wires
 *     the canonical write path through this contract.
 * - The session adapter wraps `createSessionTransitionRecorder` from
 *   `@firesquirrel/core-domain` so the canonical response snapshot
 *   (D-0.13, B14 fix) is structurally identical to what the Dexie
 *   adapter writes — both can call `buildEventResponseSnapshot` against
 *   the same shape (§9.3 shape-equivalence).
 *
 * Dependencies:
 * - `@firesquirrel/core-domain` (responseCollector + canonical types).
 * - Renderer-local violation / contact types.
 *
 * Architecture:
 * - Pure data. NO Dexie, NO network IO, NO HTTP client. The CI grep gate keeps
 *   this file forever side-effect-free.
 * - Subscribe/notify pattern is identical across both adapter flavors so
 *   consuming components don't care which one is wired.
 *
 * @contract PersistAdapter
 * Purpose: host-injected persistence surface. The renderer's session +
 * Dexie adapters both implement this.
 *
 * Fields:
 * - getValues(formId): the working `{ fieldId: FieldResponse }` map.
 * - setValues(formId, next): replace the working map atomically + notify.
 * - getCanonicalResponses(): the canonical map (B14 — preview also
 *   exposes this so Phase 6 Data slide-in can render against the same
 *   shape Dexie writes).
 * - getViolations / addViolation / updateViolation: G-023 nested
 *   violations (renderer-local; adapters translate to canonical
 *   `CanonicalQuestionResponse.violation`).
 * - getContactChanges / addContactChange: contact-list deltas.
 * - getSyncState(formId): preview returns `'synced'` always; Dexie
 *   returns the replication state ('pending'/'saving'/'synced'/'failed').
 * - reset: preview-only (session lifetime ends).
 * - subscribe(listener): notify-on-change. Returns disposer.
 *
 * Sync:
 * - Preview: lifecycle bounded to component mount.
 * - Dexie: live-write on every setValues call.
 */
import type {
  CanonicalQuestionResponse,
  FieldResponse,
  EventResponseSnapshot,
  ResolvedFormState,
  RuleEffect,
} from '@firesquirrel/core-domain';
import {
  createSessionTransitionRecorder,
  buildEventResponseSnapshot,
} from '@firesquirrel/core-domain';
import type { LoggedViolation, ContactChange } from '../violations/types';

export type SyncState = 'pending' | 'saving' | 'synced' | 'failed' | 'conflict';

export interface PersistAdapter {
  getValues(formId: string): Record<string, FieldResponse>;
  setValues(formId: string, next: Record<string, FieldResponse>): void;
  /**
   * Variant of setValues that stamps a non-default valueSource onto the
   * canonical responses. Use for `kind: 'dataBound'` (initial bound-field
   * resolve) or `kind: 'ruleComputed'` (rule-emitted writes).
   */
  setValuesWithSource(
    formId: string,
    next: Record<string, FieldResponse>,
    valueSource: { kind: 'user' | 'dataBound' | 'ruleComputed'; ruleId?: string; boundField?: string },
  ): void;
  /** B14 — canonical accessor available in both session + Dexie adapters. */
  getCanonicalResponses(): Record<string, CanonicalQuestionResponse>;
  /** Renderer-local violations (G-023). Adapter translates to canonical at finalize. */
  getViolations(): LoggedViolation[];
  addViolation(v: LoggedViolation): void;
  updateViolation(id: string, patch: Partial<LoggedViolation>): void;
  removeViolation(id: string): void;
  /** Contact-list deltas (Phase 6 Data slide-in renders these). */
  getContactChanges(): ContactChange[];
  addContactChange(c: ContactChange): void;
  /** Sync pill state (preview always 'synced'). */
  getSyncState(formId: string): SyncState;
  /** Subscribe to any change (debounced upstream). Returns disposer. */
  subscribe(listener: () => void): () => void;
  /** Reset preview-only state. No-op on Dexie adapter. */
  reset(): void;
  /**
   * Flush any pending debounced writes immediately. Hosts call this on
   * blur / visibilitychange=hidden / beforeunload. Session adapter is a
   * no-op (no debouncer). Dexie adapter commits pending text writes.
   */
  flush(): void;
  /**
   * Finalize the current session into an EventResponseSnapshot. Both
   * preview (session) and conduct (Dexie) call this at Complete time.
   */
  buildSnapshot(input: {
    eventIndex: number;
    capturedAt: string;
    capturedBy: string;
    resolvedFormStates?: Record<string, ResolvedFormState>;
    ruleEffects?: RuleEffect[];
  }): EventResponseSnapshot;
}

// ---------------------------------------------------------------------------
// Session adapter (preview / canvas-tab lifetime).
// ---------------------------------------------------------------------------

export interface SessionAdapterOpts {
  /** Inspector / actor id used for transition `changedBy`. Defaults to 'inspector'. */
  actorId?: string;
  /** Optional seed (e.g. re-opening preview with prior canvas-tab state). */
  seedResponses?: Record<string, CanonicalQuestionResponse>;
  /** Optional event index to stamp on transitions. Defaults to 0. */
  eventIndex?: number;
  /**
   * Optional initial field values to pre-seed into the adapter's working map
   * at creation time and on every `reset()` call.
   *
   * Designed for preview mode: consumers pass defaults derived from
   * `field.defaultValue` so every widget type (Toggle, CheckboxGroup,
   * RadioGroup, etc.) renders at its configured starting state and those
   * defaults are committed to the canonical response record — identical to
   * the inspector having set those values themselves.
   *
   * Implementation contract:
   * - `applyInitialFormValues` calls `recordDirtyFields` before setting
   *   `formValues`, so defaults appear in `getCanonicalResponses()` from
   *   the very first render. Rules and violation captures evaluate against
   *   them exactly as they would for user-entered values.
   * - When FieldRenderer's mount-time `useEffect` fires for a given widget,
   *   `value != null` (already in formValues) → the effect skips, preventing
   *   a duplicate recorder transition.
   * - Sentinel strings ('today', 'now') must NOT appear here; DateField /
   *   DateTimeField resolve those to wall-clock values in their own `useEffect`.
   */
  initialFormValues?: Record<string, Record<string, FieldResponse>>;
}

/**
 * @process createSessionAdapter
 *
 * Steps:
 * 1. Create a session-level transition recorder seeded from `seedResponses`.
 * 2. If `initialFormValues` is provided, call `recordDirtyFields` for each
 *    form/field (prior = {} → all fields are "dirty") then seed `formValues`.
 *    Defaults are committed to `getCanonicalResponses()` from the first render.
 * 3. Hold per-form `FieldResponse` maps in memory.
 * 4. On `setValues(formId, next)`, diff against the prior map and merge
 *    one transition per dirty field via the recorder. A field already at its
 *    default value is a no-op (before === after → skip).
 * 5. Notify subscribers.
 * 6. `buildSnapshot` delegates to `buildEventResponseSnapshot` (B14 fix).
 *
 * Inputs: SessionAdapterOpts.
 * Outputs: PersistAdapter.
 * Failure Handling: pure; no IO; no failure modes.
 * Idempotency: REQUIRED — same `setValues` call twice = one transition.
 */
export function createSessionAdapter(opts: SessionAdapterOpts = {}): PersistAdapter {
  const actorId = opts.actorId ?? 'inspector';
  const eventIndex = opts.eventIndex ?? 0;
  const recorder = createSessionTransitionRecorder(opts.seedResponses ?? {});

  const formValues = new Map<string, Record<string, FieldResponse>>();
  const violations: LoggedViolation[] = [];
  const contactChanges: ContactChange[] = [];
  const listeners = new Set<() => void>();

  // Pre-seed the working form-values map from declared field defaults.
  // Called at creation and, after recorder.reset(), inside `reset()` so
  // the adapter always returns to the template-configured starting state.
  // Records through the canonical recorder first (prior = {} since formValues
  // is not yet set for this formId) so defaults appear in
  // getCanonicalResponses() exactly as if the inspector had set them.
  function applyInitialFormValues() {
    if (!opts.initialFormValues) return;
    for (const [formId, fieldMap] of Object.entries(opts.initialFormValues)) {
      // Sentinel strings ('today', 'now') are stored in the working map so
      // DateField / DateTimeField widgets receive and resolve them, but are
      // intentionally excluded from the canonical recorder — the recorder must
      // never hold stale sentinel strings.
      const canonicalFields: Record<string, FieldResponse> = {};
      for (const [fieldId, fieldResp] of Object.entries(fieldMap)) {
        if (fieldResp.value !== 'today' && fieldResp.value !== 'now') {
          canonicalFields[fieldId] = fieldResp;
        }
      }
      if (Object.keys(canonicalFields).length > 0) {
        // Record before setting formValues so dirty-check sees prior = {}.
        recordDirtyFields(formId, canonicalFields, { kind: 'ruleComputed', ruleId: 'field-default' });
      }
      // Store ALL fields (including sentinels) so widgets receive them.
      formValues.set(formId, { ...fieldMap });
    }
  }
  applyInitialFormValues();

  function notify() {
    for (const l of listeners) l();
  }

  function recordDirtyFields(
    formId: string,
    next: Record<string, FieldResponse>,
    valueSource: { kind: 'user' | 'dataBound' | 'ruleComputed'; ruleId?: string; boundField?: string } = { kind: 'user' },
  ) {
    const prior = formValues.get(formId) ?? {};
    const dirtyIds = new Set<string>([
      ...Object.keys(prior),
      ...Object.keys(next),
    ]);
    for (const id of dirtyIds) {
      const before = prior[id];
      const after = next[id];
      if (!after) continue;
      // Skip when the persisted state is structurally identical (no transition).
      if (before && before.value === after.value && before.isNA === after.isNA) continue;
      recorder.record(id, {
        eventIndex,
        actorId,
        nextValue: after.value,
        nextIsNA: after.isNA ?? false,
        nextIsHidden: false,
        reason: valueSource.kind === 'user' ? 'autosave' : `value-source:${valueSource.kind}`,
        valueSource,
      });
    }
  }

  return {
    getValues(formId) {
      return formValues.get(formId) ?? {};
    },
    setValues(formId, next) {
      recordDirtyFields(formId, next);
      formValues.set(formId, { ...next });
      notify();
    },
    setValuesWithSource(formId, next, valueSource) {
      recordDirtyFields(formId, next, valueSource);
      formValues.set(formId, { ...next });
      notify();
    },
    getCanonicalResponses() {
      return recorder.getCanonicalResponses();
    },
    getViolations() {
      return violations.slice();
    },
    addViolation(v) {
      violations.push(v);
      notify();
    },
    updateViolation(id, patch) {
      const idx = violations.findIndex((x) => x._id === id);
      if (idx < 0) return;
      violations[idx] = { ...violations[idx], ...patch };
      notify();
    },
    removeViolation(id) {
      const idx = violations.findIndex((x) => x._id === id);
      if (idx < 0) return;
      violations.splice(idx, 1);
      notify();
    },
    getContactChanges() {
      return contactChanges.slice();
    },
    addContactChange(c) {
      contactChanges.push(c);
      notify();
    },
    getSyncState() {
      // D-0.14 — preview is always 'synced'; the session is pure local state.
      return 'synced';
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    reset() {
      formValues.clear();
      violations.length = 0;
      contactChanges.length = 0;
      recorder.reset();
      applyInitialFormValues(); // Re-seed after recorder.reset() so defaults re-populate canonical.
      notify();
    },
    flush() {
      // Session adapter writes synchronously on every `setValues`; nothing
      // to flush. Method exists for adapter-symmetry with the Dexie adapter.
    },
    buildSnapshot(input) {
      return buildEventResponseSnapshot({
        eventIndex: input.eventIndex,
        capturedAt: input.capturedAt,
        capturedBy: input.capturedBy,
        responses: recorder.getCanonicalResponses(),
        resolvedFormStates: input.resolvedFormStates,
        ruleEffects: input.ruleEffects,
      });
    },
  };
}
